// Presentation-only Persian formatting. Never use these outputs for API writes.
const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
export const toPersianDigits = value => String(value ?? "").replace(/[0-9٠-٩]/g, char => {
  const code = char.charCodeAt(0);
  const digit = code >= 0x0660 && code <= 0x0669 ? code - 0x0660 : code - 48;
  return FA_DIGITS[digit];
});

const persianCalendar = new Intl.DateTimeFormat("en-US-u-ca-persian-nu-latn", {
  timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit",
});
export function toJalaliDate(year, month, day) {
  const y = Number(year), m = Number(month), d = Number(day);
  if (y < 1600 || y > 2500 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  const parts = Object.fromEntries(persianCalendar.formatToParts(date).map(p => [p.type, p.value]));
  return toPersianDigits(`${parts.year}/${parts.month}/${parts.day}`);
}

// Dates emitted by the API or created by toLocaleDateString may use separators
// YYYY-MM-DD, YYYY/MM/DD, or YYYY.MM.DD. Convert full dates, never bare years.
const datePattern = /(^|[^\d])((?:1[6-9]\d{2}|2[0-4]\d{2}))([-/.])(0?[1-9]|1[0-2])\3(0?[1-9]|[12]\d|3[01])(?=$|[^\d])/g;
// Day-first Gregorian dates frequently appear in report exports.
const dayFirstDatePattern = /(^|[^\d])(0?[1-9]|[12]\d|3[01])([/.])(0?[1-9]|1[0-2])\3((?:1[6-9]\d{2}|2[0-4]\d{2}))(?=$|[^\d])/g;
const protectedTokens = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|\b[a-f0-9]{8}-[a-f0-9-]{27,}\b)/gi;
export function localizePersianDisplay(value) {
  return String(value ?? "").split(protectedTokens).map((part, index) => {
    if (index % 2) return part;
    const yearFirst = part.replace(datePattern, (match, prefix, year, sep, month, day) =>
      prefix + (toJalaliDate(year, month, day) ?? match.slice(prefix.length))
    );
    return toPersianDigits(yearFirst.replace(dayFirstDatePattern, (match, prefix, day, sep, month, year) =>
      prefix + (toJalaliDate(year, month, day) ?? match.slice(prefix.length))
    ));
  }).join("");
}

export function fromJalaliDate(year, month, day) {
  const jy = Number(year), jm = Number(month), jd = Number(day);
  if (!Number.isInteger(jy) || jy < 1200 || jy > 1700 || !Number.isInteger(jm) || jm < 1 || jm > 12 || !Number.isInteger(jd) || jd < 1 || jd > 31) return null;
  // Calendar-aware binary search avoids hardcoded leap-year tables.
  let lo = Math.floor(Date.UTC(jy + 620, 0, 1) / 86400000);
  let hi = Math.floor(Date.UTC(jy + 623, 0, 1) / 86400000);
  const wanted = jy * 10000 + jm * 100 + jd;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const parts = Object.fromEntries(persianCalendar.formatToParts(new Date(mid * 86400000)).map(p => [p.type, p.value]));
    const actual = Number(parts.year) * 10000 + Number(parts.month) * 100 + Number(parts.day);
    if (actual === wanted) return new Date(mid * 86400000).toISOString().slice(0, 10);
    if (actual < wanted) lo = mid + 1;
    else hi = mid - 1;
  }
  return null;
}

export function jalaliDateParts(isoDate) {
  const match = String(isoDate ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const formatted = toJalaliDate(match[1], match[2], match[3]);
  if (!formatted) return null;
  return formatted.split("/").map(n => Number(n.replace(/[۰-۹]/g, d => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))));
}
